import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map } from 'rxjs';
import { ExtendedProfile } from './common/ExtendedProfile';
import { AppProfileService } from './services/profile.service';
import { AuthorizeService } from './auth/authorize.service';
import { BreadcrumbService } from './services/breadcrumb.service';
import { FooterComponent } from './components/footer/footer.component';
import { ShopNavComponent } from './components/shop-nav/shop-nav.component';
import { LandingPageComponent } from './components/landing-page/landing-page.component';
import { IdleTimerComponent } from './components/idle-timer/idle-timer.component';
import { MainNavComponent } from './components/main-nav/main-nav.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { LanguagePreferenceService } from './services/language-preference.service';

@Component({
    imports: [
    RouterModule,
    MainNavComponent,
    ShopNavComponent,
    LandingPageComponent,
    IdleTimerComponent,
    FooterComponent,
    LoadingSpinnerComponent,
    BreadcrumbComponent
],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  private router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  private profileService = inject(AppProfileService);
  private authService = inject(AuthorizeService);
  breadcrumbService = inject(BreadcrumbService);
  private languagePreference = inject(LanguagePreferenceService);

  private destroyRef = inject(DestroyRef);

  public showLandingPage: boolean = false;
  title = 'getlocal-fe';
  public profile: ExtendedProfile;
  public profileLoading: boolean = false;
  /**
   * True once the first profile fetch has settled (with or without a profile).
   * The blocking spinner is limited to that first load — later refreshes must not
   * tear down the router outlet, or a profile-less user on the welcome page ends up
   * in a destroy/re-create loop (welcome ngOnInit → refresh → spinner → destroy …).
   */
  public profileSettled: boolean = false;

  /** True while the active route belongs to the "Mein Konto" section. */
  get showAccountNav(): boolean {
    return this.breadcrumbService.section() === 'mein-konto';
  }

  ngOnInit() {
    // retailerMode was previously initialised in ShopNavComponent, which is now
    // only rendered contextually — initialise it here so it still runs on load.
    const storedTenantMode = sessionStorage.getItem('retailerMode');
    this.authService.setRetailerMode(storedTenantMode == '1');

    this.profileService.profileLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => {
        this.profileLoading = loading;
      });

    // #178 slice B: the cached language follows the bundle from the first paint, so an anonymous
    // visitor routed to /en/ by nginx does not leave lang.js's guess behind contradicting it.
    this.languagePreference.rememberBundle();

    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.profile = p;
      this.profileSettled = true;
      // #178 slice B: once a profile exists, its stored language decides which bundle this user
      // should be reading. Acts at most once per load, and only when the two disagree.
      this.languagePreference.syncWithProfile(p);
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => event as NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.showLandingPage = event.url === '/';
      });
  }
}
